import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toPng } from "html-to-image";
import { LogOut, ShieldCheck, Fingerprint, Boxes, Users, FileText, Copy, Check, ExternalLink, Home, IdCard, Package, Download, Loader2, Truck, Camera } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import api, { LOGO_URL } from "@/lib/api";
import MembershipIDCard from "@/components/MembershipIDCard";
import { BENEFITS } from "@/data/content";

const Stat = ({ label, value, mono }) => (
  <div className="border border-gold/15 bg-ink-surface p-6">
    <p className="font-mono text-[10px] tracking-[0.24em] uppercase text-gold/60">{label}</p>
    <p className={`mt-3 text-gold-text ${mono ? "font-mono text-sm break-all" : "font-serif text-2xl"}`}>{value}</p>
  </div>
);

export default function Vault() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [records, setRecords] = useState([]);
  const [copied, setCopied] = useState(false);
  const [idCard, setIdCard] = useState(null);
  const [orders, setOrders] = useState([]);
  const [ordering, setOrdering] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const cardRef = useRef(null);
  const photoInputRef = useRef(null);

  useEffect(() => {
    const load = async () => {
      try {
        const [s, r, c, o] = await Promise.all([
          api.get("/vault/summary"),
          api.get("/genealogy/my"),
          api.get("/vault/id-card"),
          api.get("/id-orders/my"),
        ]);
        setSummary(s.data);
        setRecords(r.data);
        setIdCard(c.data);
        setOrders(o.data);
      } catch (e) {
        /* handled by protected route */
        console.error("Vault data load failed:", e);
      }
    };
    load();
  }, []);

  const downloadCard = async () => {
    if (!cardRef.current) return;
    try {
      const dataUrl = await toPng(cardRef.current, { pixelRatio: 3, cacheBust: true });
      const link = document.createElement("a");
      link.download = `7GIT-Membership-ID-${idCard?.credential_id || "card"}.png`;
      link.href = dataUrl;
      link.click();
      toast.success("Digital ID downloaded");
    } catch (e) {
      toast.error("Could not export the card.");
    }
  };

  const uploadPhoto = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploadingPhoto(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const res = await api.post("/vault/id-photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setIdCard((c) => ({ ...c, photo_url: res.data.photo_url }));
      toast.success("ID photo updated");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not upload photo.");
    } finally {
      setUploadingPhoto(false);
      if (photoInputRef.current) photoInputRef.current.value = "";
    }
  };

  const orderPhysical = async () => {
    setOrdering(true);
    try {
      const res = await api.post("/payments/checkout", { origin_url: window.location.origin });
      window.location.href = res.data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start checkout.");
      setOrdering(false);
    }
  };

  const doLogout = async () => {
    await logout();
    navigate("/");
  };

  const copyCred = () => {
    navigator.clipboard.writeText(summary?.credential_id || "");
    setCopied(true);
    toast.success("Credential ID copied");
    setTimeout(() => setCopied(false), 1600);
  };

  return (
    <div className="min-h-screen bg-[#050505] flex flex-col lg:flex-row">
      {/* Sidebar */}
      <aside className="lg:w-72 lg:min-h-screen border-b lg:border-b-0 lg:border-r border-gold/15 bg-ink-deep p-6 flex lg:flex-col justify-between">
        <div>
          <div className="flex items-center gap-3">
            <span className="flex items-center justify-center w-9 h-9 border border-gold/40 rounded-full font-serif text-lg text-gold-gradient">7</span>
            <div className="leading-none hidden lg:block">
              <p className="font-serif text-sm tracking-[0.14em]">THE VAULT</p>
              <p className="font-mono text-[8px] tracking-[0.3em] uppercase text-gold/60 mt-1">Modern Freedmen Bureau</p>
            </div>
          </div>
          <nav className="hidden lg:block mt-12 space-y-1">
            {[
              { icon: Boxes, label: "Overview" },
              { icon: FileText, label: "Ancestry Records" },
              { icon: Fingerprint, label: "Credentials" },
              { icon: Users, label: "Mutual Aid" },
            ].map((n, i) => (
              <div key={n.label} className={`flex items-center gap-3 px-4 py-3 font-mono text-[11px] tracking-[0.14em] uppercase ${i === 0 ? "bg-gold/10 text-gold border-l border-gold" : "text-[#7a766f]"}`}>
                <n.icon size={15} /> {n.label}
              </div>
            ))}
          </nav>
        </div>
        <div className="flex lg:flex-col gap-3">
          <button onClick={() => navigate("/")} data-testid="vault-home-btn" className="flex items-center gap-2 px-4 py-3 border border-gold/20 text-[#A19D98] hover:text-gold hover:border-gold/40 font-mono text-[10px] tracking-[0.18em] uppercase transition-colors">
            <Home size={14} /> <span className="hidden lg:inline">Public Portal</span>
          </button>
          <button onClick={doLogout} data-testid="vault-logout-btn" className="flex items-center gap-2 px-4 py-3 border border-gold/20 text-[#A19D98] hover:text-gold hover:border-gold/40 font-mono text-[10px] tracking-[0.18em] uppercase transition-colors">
            <LogOut size={14} /> <span className="hidden lg:inline">Sign Out</span>
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 p-6 sm:p-10">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <p className="font-mono text-[10px] tracking-[0.28em] uppercase text-gold/60">Welcome back</p>
              <h1 className="font-serif font-light text-4xl sm:text-5xl mt-2">{user?.name}</h1>
            </div>
            <div className="flex items-center gap-2 px-4 py-2 border border-gold/25 font-mono text-[10px] tracking-[0.2em] uppercase text-gold">
              <ShieldCheck size={14} /> {summary?.citizenship_status || "Active"}
            </div>
          </div>

          {/* Credential card */}
          <div className="mt-10 relative border border-gold/25 bg-gradient-to-br from-ink-surface to-ink-deep p-8 overflow-hidden">
            <img src={LOGO_URL} alt="" className="absolute -right-16 -top-10 w-72 opacity-[0.06] pointer-events-none" />
            <p className="font-mono text-[10px] tracking-[0.28em] uppercase text-gold/60">Digital Economic Citizenship Credential</p>
            <div className="flex items-center gap-4 mt-4">
              <span className="font-serif text-4xl sm:text-5xl text-gold-gradient tracking-wide">{summary?.credential_id || "—"}</span>
              <button onClick={copyCred} data-testid="copy-credential" className="text-gold/60 hover:text-gold transition-colors">
                {copied ? <Check size={18} /> : <Copy size={18} />}
              </button>
            </div>
            <div className="grid sm:grid-cols-2 gap-6 mt-8">
              <div>
                <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-[#7a766f]">Lineage Anchor</p>
                <p className="font-mono text-xs text-gold-text break-all mt-2">{summary?.lineage_anchor}</p>
              </div>
              <div>
                <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-[#7a766f]">Mutual-Aid Network</p>
                <p className="font-sans text-sm text-[#F5F0EB] mt-2">{summary?.mutual_aid_network}</p>
              </div>
            </div>
          </div>

          {/* Stats */}
          <div className="grid sm:grid-cols-3 gap-4 mt-4">
            <Stat label="Records Anchored" value={summary?.records_anchored ?? 0} />
            <Stat label="Registry" value="Immutable · SHA-256" mono />
            <Stat label="Sovereignty" value="Independent" />
          </div>

          {/* Records table */}
          <div className="mt-12">
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-serif text-3xl">Blockchain-Backed Ancestry Records</h2>
              <button onClick={() => navigate("/eligibility?tab=verify")} data-testid="vault-add-record" className="font-mono text-[10px] tracking-[0.18em] uppercase text-gold border-b border-gold/40 pb-1 hover:border-gold transition-colors">
                + Anchor new record
              </button>
            </div>
            {records.length === 0 ? (
              <div className="border border-gold/15 bg-ink-surface p-12 text-center">
                <FileText size={26} className="text-gold/50 mx-auto" strokeWidth={1.3} />
                <p className="font-sans text-sm text-[#A19D98] mt-4">No records anchored yet.</p>
                <button onClick={() => navigate("/eligibility?tab=verify")} className="mt-5 px-6 py-3 bg-gold text-black font-mono text-[10px] tracking-[0.2em] uppercase hover:bg-gold-text transition-colors">
                  Verify Genealogy
                </button>
              </div>
            ) : (
              <div className="border border-gold/15 overflow-x-auto" data-testid="records-table">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="bg-ink-surface">
                      {["Title", "Lineage", "Block", "Hash", "Status", ""].map((h) => (
                        <th key={h} className="text-left font-mono text-[10px] tracking-[0.18em] uppercase text-gold/60 px-5 py-4 border-b border-gold/15">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {records.map((r) => (
                      <tr key={r.id} className="border-b border-gold/10 hover:bg-gold/[0.03] transition-colors">
                        <td className="px-5 py-4 font-sans text-sm text-[#F5F0EB]">{r.title}</td>
                        <td className="px-5 py-4 font-sans text-xs text-[#A19D98]">{r.lineage}</td>
                        <td className="px-5 py-4 font-mono text-xs text-gold-text">#{r.block_number}</td>
                        <td className="px-5 py-4 font-mono text-xs text-[#7a766f] max-w-[160px] truncate">{r.block_hash}</td>
                        <td className="px-5 py-4">
                          <span className="font-mono text-[10px] tracking-[0.12em] uppercase text-gold flex items-center gap-1"><ShieldCheck size={12} /> Anchored</span>
                        </td>
                        <td className="px-5 py-4">
                          <a href={`${api.defaults.baseURL}/files/${r.storage_path}`} target="_blank" rel="noreferrer" className="text-gold/60 hover:text-gold transition-colors" title="View document">
                            <ExternalLink size={15} />
                          </a>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Membership ID */}
          <div className="mt-12" data-testid="membership-id-section">
            <div className="flex items-center gap-3 mb-6">
              <IdCard size={22} className="text-gold" strokeWidth={1.3} />
              <h2 className="font-serif text-3xl">Membership ID</h2>
            </div>
            <div className="grid lg:grid-cols-2 gap-8 items-start">
              {/* Digital card */}
              <div className="border border-gold/15 bg-ink-surface p-8">
                <p className="font-mono text-[10px] tracking-[0.24em] uppercase text-gold/60">Digital ID · Issued</p>
                <div className="mt-6 flex justify-center">
                  <MembershipIDCard ref={cardRef} data={idCard} />
                </div>
                <input
                  ref={photoInputRef}
                  type="file"
                  accept=".jpg,.jpeg,.png,.webp"
                  className="hidden"
                  data-testid="id-photo-input"
                  onChange={uploadPhoto}
                />
                <button
                  onClick={() => photoInputRef.current?.click()}
                  disabled={uploadingPhoto}
                  data-testid="upload-photo-btn"
                  className="w-full mt-6 flex items-center justify-center gap-3 px-6 py-3.5 border border-gold/40 text-gold font-mono text-[11px] tracking-[0.2em] uppercase hover:bg-gold hover:text-black transition-colors disabled:opacity-60"
                >
                  {uploadingPhoto ? <><Loader2 size={15} className="animate-spin" /> Uploading…</> : <><Camera size={15} /> {idCard?.photo_url ? "Change ID Photo" : "Upload ID Photo"}</>}
                </button>
                <button
                  onClick={downloadCard}
                  data-testid="download-id-btn"
                  className="w-full mt-3 flex items-center justify-center gap-3 px-6 py-4 bg-gold text-black font-mono text-[11px] tracking-[0.2em] uppercase hover:bg-gold-text transition-colors"
                >
                  <Download size={15} /> Download Digital ID
                </button>
              </div>

              {/* Physical order */}
              <div className="border border-gold/25 bg-ink-surface p-8 h-full flex flex-col">
                <div className="flex items-center gap-3">
                  <Package size={22} className="text-gold" strokeWidth={1.3} />
                  <h3 className="font-serif text-2xl">Physical ID Card</h3>
                </div>
                <p className="font-serif text-4xl text-gold-gradient mt-4">$49.00</p>
                <ul className="mt-6 space-y-3 flex-1">
                  {["Embossed metallic card", "Secure worldwide shipping", "Tamper-evident credential", "Everything in the Digital ID"].map((f) => (
                    <li key={f} className="flex items-start gap-3">
                      <Check size={15} className="text-gold mt-0.5 shrink-0" />
                      <span className="font-sans text-sm text-[#C9C4BD]">{f}</span>
                    </li>
                  ))}
                </ul>
                <button
                  onClick={orderPhysical}
                  disabled={ordering}
                  data-testid="order-physical-btn"
                  className="w-full mt-8 flex items-center justify-center gap-3 px-6 py-4 bg-gold text-black font-mono text-[11px] tracking-[0.2em] uppercase hover:bg-gold-text transition-colors disabled:opacity-60"
                >
                  {ordering ? <><Loader2 size={15} className="animate-spin" /> Redirecting…</> : <><Package size={15} /> Order Physical Card</>}
                </button>

                {orders.length > 0 && (
                  <div className="mt-8 border-t border-gold/10 pt-6" data-testid="id-orders-list">
                    <p className="font-mono text-[10px] tracking-[0.24em] uppercase text-gold/60 mb-4">Your Orders</p>
                    <div className="space-y-3">
                      {orders.map((o) => (
                        <div key={o.id} className="flex items-center justify-between gap-3">
                          <div className="flex items-center gap-2">
                            <Truck size={14} className="text-gold/70" />
                            <span className="font-sans text-sm text-[#F5F0EB] capitalize">{o.card_type} card</span>
                          </div>
                          <span className="font-mono text-[10px] tracking-[0.14em] uppercase text-gold">{o.status.replace(/_/g, " ")}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Member benefits */}
          <div className="mt-12" data-testid="vault-benefits">
            <div className="flex items-center gap-3 mb-6">
              <ShieldCheck size={22} className="text-gold" strokeWidth={1.3} />
              <h2 className="font-serif text-3xl">Your Member Benefits</h2>
            </div>
            <div className="grid sm:grid-cols-2 gap-x-10 border border-gold/15 bg-ink-surface px-8">
              {BENEFITS.map((b) => (
                <div key={b.title} className="flex items-start gap-4 py-5 border-b border-gold/10">
                  <b.icon size={18} className="text-gold shrink-0 mt-0.5" strokeWidth={1.4} />
                  <div>
                    <h4 className="font-sans text-sm text-[#F5F0EB]">{b.title}</h4>
                    <p className="font-sans text-xs text-[#A19D98] leading-relaxed mt-1">{b.text}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>


          {/* Mutual aid */}
          <div className="mt-12 border border-gold/15 bg-ink-surface p-8">
            <div className="flex items-center gap-3">
              <Users size={22} className="text-gold" strokeWidth={1.3} />
              <h2 className="font-serif text-2xl">Mutual-Aid Network</h2>
            </div>
            <p className="font-sans text-sm text-[#A19D98] leading-relaxed mt-4 max-w-2xl">
              As a verified member, you are connected to the {summary?.mutual_aid_network || "Trust"} — a
              network of descendants pooling knowledge, resources, and support in the spirit of Independent
              Economic Sovereignty. Full network directory access is extended after credential maturity.
            </p>
          </div>
        </motion.div>
      </main>
    </div>
  );
}
